import React, { useState, useEffect } from 'react';
import { 
  FileText, RefreshCw, AlertCircle, Filter,
  User, Smartphone, Camera, Settings, LogOut, Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import api from '@/lib/api';

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Get icon for action type
const getActionIcon = (action) => {
  switch (action) {
    case 'device_removed':
      return <Smartphone className="h-4 w-4 text-red-400" />;
    case 'user_updated':
      return <User className="h-4 w-4 text-blue-400" />;
    case 'settings_updated':
      return <Settings className="h-4 w-4 text-purple-400" />;
    case 'camera_created':
    case 'camera_updated':
    case 'camera_deleted':
      return <Camera className="h-4 w-4 text-orange-400" />;
    case 'session_kicked':
      return <LogOut className="h-4 w-4 text-red-400" />;
    default:
      return <FileText className="h-4 w-4 text-muted-foreground" />;
  }
};

// Get badge color for action
const getActionBadgeColor = (action) => {
  if (action.includes('deleted') || action.includes('removed') || action.includes('kicked')) {
    return 'bg-red-500/20 text-red-400';
  }
  if (action.includes('created')) {
    return 'bg-green-500/20 text-green-400';
  }
  if (action.includes('updated')) {
    return 'bg-blue-500/20 text-blue-400';
  }
  return 'bg-secondary text-muted-foreground';
};

// Format action name for display
const formatAction = (action) => {
  return action
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Format details for display
const formatDetails = (details) => {
  if (!details || Object.keys(details).length === 0) return null;
  
  return Object.entries(details).map(([key, value]) => {
    const displayKey = key.replace(/_/g, ' ');
    let displayValue = value;
    
    if (typeof value === 'boolean') {
      displayValue = value ? 'Yes' : 'No';
    } else if (value === null) {
      displayValue = 'None';
    } else if (typeof value === 'object') {
      displayValue = JSON.stringify(value);
    }
    
    return (
      <span key={key} className="inline-flex items-center gap-1 mr-3">
        <span className="text-muted-foreground">{displayKey}:</span>
        <span className="font-mono">{String(displayValue)}</span>
      </span>
    );
  });
};

// Audit log entry component
const AuditLogEntry = ({ entry }) => (
  <div className="flex items-start gap-4 p-4 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary">
      {getActionIcon(entry.action)}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge className={`text-xs ${getActionBadgeColor(entry.action)}`}>
          {formatAction(entry.action)}
        </Badge>
        <span className="text-sm text-muted-foreground">by</span>
        <span className="font-medium">{entry.admin_user || 'System'}</span>
        {entry.target_id && (
          <>
            <span className="text-sm text-muted-foreground">on</span>
            <Badge variant="outline" className="text-xs font-mono">
              {entry.target_type}: {entry.target_id}
            </Badge>
          </>
        )}
      </div>
      {entry.details && Object.keys(entry.details).length > 0 && (
        <div className="mt-2 text-xs">
          {formatDetails(entry.details)}
        </div>
      )}
      <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
        <span>{formatDate(entry.timestamp)}</span>
        {entry.ip_address && (
          <span className="font-mono">{entry.ip_address}</span>
        )}
      </div>
    </div>
  </div>
);

export function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [filterAction, setFilterAction] = useState('all');
  const [filterAdmin, setFilterAdmin] = useState('');
  const [filterTargetType, setFilterTargetType] = useState('all');

  const fetchAuditLog = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = { limit: 100 };
      if (filterAction !== 'all') params.action = filterAction;
      if (filterAdmin) params.admin_user = filterAdmin;
      if (filterTargetType !== 'all') params.target_type = filterTargetType;
      
      const data = await api.getAuditLog(params);
      setEntries(data.entries || data || []);
    } catch (err) {
      setError(err.message || 'Failed to load audit log');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAction, filterTargetType]);

  // Search on admin filter with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (filterAdmin !== '') {
        fetchAuditLog();
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAdmin]);

  if (error && entries.length === 0) {
    return (
      <div className="space-y-6" data-testid="audit-log-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Audit Log</h1>
            <p className="text-muted-foreground">Track admin actions and changes</p>
          </div>
        </div>
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="flex items-center gap-4 p-6">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <div className="flex-1">
              <p className="font-medium">Failed to load audit log</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
            <Button variant="outline" onClick={fetchAuditLog}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Audit Log</h1>
          <p className="text-muted-foreground">Track admin actions and changes</p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchAuditLog}
          disabled={isLoading}
          data-testid="refresh-audit-log-btn"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-border bg-card">
        <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filters:</span>
          </div>
          <Select value={filterAction} onValueChange={setFilterAction}>
            <SelectTrigger className="w-44" data-testid="filter-action-select">
              <SelectValue placeholder="All Actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="device_removed">Device Removed</SelectItem>
              <SelectItem value="user_updated">User Updated</SelectItem>
              <SelectItem value="settings_updated">Settings Updated</SelectItem>
              <SelectItem value="camera_created">Camera Created</SelectItem>
              <SelectItem value="camera_updated">Camera Updated</SelectItem>
              <SelectItem value="camera_deleted">Camera Deleted</SelectItem>
              <SelectItem value="session_kicked">Session Kicked</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterTargetType} onValueChange={setFilterTargetType}>
            <SelectTrigger className="w-36" data-testid="filter-target-select">
              <SelectValue placeholder="All Targets" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Targets</SelectItem>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="device">Device</SelectItem>
              <SelectItem value="camera">Camera</SelectItem>
              <SelectItem value="settings">Settings</SelectItem>
              <SelectItem value="session">Session</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Filter by admin..."
            value={filterAdmin}
            onChange={(e) => setFilterAdmin(e.target.value)}
            className="w-44"
            data-testid="filter-admin-input"
          />
        </CardContent>
      </Card>

      {/* Log Entries */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Recent Activity
            <Badge variant="secondary" className="ml-2">
              {entries.length} entries
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-20 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground/30 mb-4" />
              <p className="text-lg font-medium">No audit log entries</p>
              <p className="text-sm text-muted-foreground">
                Admin actions will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {entries.map((entry, i) => (
                <AuditLogEntry key={entry._id || i} entry={entry} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default AuditLog;
